/**
 * Error Parsing Library - Formatters
 *
 * Utilities for formatting parsed errors for display and logging.
 */

import type { ParsedTransactionError, SuggestedAction } from './types'

/**
 * Format an error for copying to clipboard or support tickets.
 * Includes all technical details.
 */
export function formatForSupport(error: ParsedTransactionError): string {
  const parts: string[] = []

  // Header
  parts.push('=== Transaction Error Report ===')
  parts.push('')

  // Basic info
  parts.push(`Category: ${error.category}`)
  parts.push(`Severity: ${error.severity}`)
  if (error.errorCode) {
    parts.push(`Error Code: ${error.errorCode}`)
  }
  parts.push('')

  // Messages
  parts.push('User Message:')
  parts.push(error.userMessage)
  parts.push('')
  parts.push('Technical Message:')
  parts.push(error.technicalMessage)
  parts.push('')

  // Context
  if (error.context) {
    parts.push('Context:')
    if (error.context.txHash) {
      parts.push(`  Transaction Hash: ${error.context.txHash}`)
    }
    if (error.context.contractAddress) {
      parts.push(`  Contract: ${error.context.contractAddress}`)
    }
    if (error.context.functionName) {
      parts.push(`  Function: ${error.context.functionName}`)
    }
    if (error.context.errorSelector) {
      parts.push(`  Error Selector: 0x${error.context.errorSelector}`)
    }
    if (error.context.blockNumber) {
      parts.push(`  Block: ${error.context.blockNumber}`)
    }
    parts.push('')
  }

  // Recovery info
  parts.push(`Recoverable: ${error.isRecoverable ? 'Yes' : 'No'}`)
  parts.push(`Suggested Action: ${getActionText(error.suggestedAction)}`)
  parts.push('')

  // Stack trace
  if (error.originalError.stack) {
    parts.push('Stack Trace:')
    parts.push(error.originalError.stack)
  }

  // Raw data
  if (error.context?.rawData) {
    parts.push('')
    parts.push('Raw Error Data:')
    parts.push(error.context.rawData)
  }

  return parts.join('\n')
}

/**
 * Get a human-readable action text.
 */
export function getActionText(action: SuggestedAction): string {
  const texts: Record<SuggestedAction, string> = {
    retry: 'Try again',
    'retry-later': 'Wait a moment and try again',
    'adjust-amount': 'Try a smaller amount',
    'adjust-slippage': 'Increase slippage tolerance',
    'add-collateral': 'Add more collateral',
    'reduce-borrow': 'Reduce your borrow amount',
    'add-funds': 'Add funds to your wallet',
    'check-network': 'Check your network connection',
    'switch-network': 'Switch to the correct network',
    'contact-support': 'Contact support for help',
    'approve-token': 'Approve token spending first',
    none: '',
  }
  return texts[action]
}

